import {useEffect, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, useNavigate} from 'react-router-dom';
import {
  getInspection,
  generateReport,
  saveReportVersion,
  getReportVersions,
  getReportVersion,
  getEmailTemplates,
  sendReportEmail,
  generatePublicToken,
  getConclusieItems,
  saveConclusies,
} from '../Reducers/actions';
import Modal from '../../../Components/Modal/Modal';
import html2canvas from 'html2canvas';
import {jsPDF} from 'jspdf';
import autoTable from 'jspdf-autotable';
import './rapport.css';

const InspectionReportPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {id} = useParams();
  const {loading} = useSelector(state => state.zwembadinspecties);
  const [reportData, setReportData] = useState(null);
  const reportRef = useRef();

  // Version management state
  const [versions, setVersions] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showVersionsDropdown, setShowVersionsDropdown] = useState(false);
  const [versionNotes, setVersionNotes] = useState('');
  const [savingVersion, setSavingVersion] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailData, setEmailData] = useState({
    to: '',
    cc: '',
    subject: '',
    body: '',
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [includeCertificate, setIncludeCertificate] = useState(false);
  const [includePublicLink, setIncludePublicLink] = useState(false);
  const [publicReportUrl, setPublicReportUrl] = useState(null);

  // Conclusie state
  const [conclusieItems, setConclusieItems] = useState([]);
  const [selectedConclusies, setSelectedConclusies] = useState([]);
  const [certificaatUitgeven, setCertificaatUitgeven] = useState(null);
  const [savingConclusies, setSavingConclusies] = useState(false);
  const [showConclusiePanel, setShowConclusiePanel] = useState(false);
  const [conclusieTagFilter, setConclusieTagFilter] = useState('');

  useEffect(() => {
    // Haal inspectie data op
    dispatch(getInspection(id));

    // Haal rapport data op
    dispatch(generateReport(id)).then(response => {
      if (response && response.result) {
        setReportData(response.result);
        // Laad geselecteerde conclusies van de inspectie
        const insp = response.result.inspection;
        if (insp.selected_conclusies && insp.selected_conclusies.length > 0) {
          setSelectedConclusies(insp.selected_conclusies.map(c => c._id || c));
          setCertificaatUitgeven(insp.certificaat_uitgeven);
        }
      }
    });

    // Haal beschikbare conclusie items op
    dispatch(getConclusieItems()).then(response => {
      if (response && response.results) {
        setConclusieItems(response.results);
      }
    });
  }, [dispatch, id]);

  const formatDate = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Conclusie toggle handler
  const handleToggleConclusie = (itemId) => {
    setSelectedConclusies(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Bepaal automatisch of certificaat uitgegeven kan worden op basis van geselecteerde conclusies
  useEffect(() => {
    if (selectedConclusies.length > 0 && conclusieItems.length > 0) {
      const selectedItems = conclusieItems.filter(item => selectedConclusies.includes(item._id));
      // Certificaat kan uitgegeven worden als ALLE geselecteerde conclusies dat toestaan
      const canIssueCertificate = selectedItems.length > 0 && selectedItems.every(item => item.certificaat_uitgeven);
      setCertificaatUitgeven(canIssueCertificate);
    }
  }, [selectedConclusies, conclusieItems]);

  // Conclusies opslaan
  const handleSaveConclusies = async () => {
    setSavingConclusies(true);
    try {
      await dispatch(saveConclusies(id, {
        selected_conclusies: selectedConclusies,
        certificaat_uitgeven: certificaatUitgeven
      }));
      // Herlaad rapport om conclusies te refreshen
      const response = await dispatch(generateReport(id));
      if (response && response.result) {
        setReportData(response.result);
      }
      setShowConclusiePanel(false);
    } catch (error) {
      console.error("Fout bij opslaan conclusies:", error);
      alert("Fout bij opslaan conclusies");
    } finally {
      setSavingConclusies(false);
    }
  };

  // Helper: haal geselecteerde conclusie objecten op
  const getSelectedConclusieObjects = () => {
    if (!reportData?.inspection?.selected_conclusies) return [];
    return reportData.inspection.selected_conclusies;
  };

  const [downloadingPDF, setDownloadingPDF] = useState(false);

  /**
   * Extract tabel data uit HTML tabel voor autoTable
   * Retourneert headers, body data en een map van afbeelding URLs per cel
   */
  /**
   * Converteer HTML naar tekst met behoud van opmaak/regelafbrekingen
   */
  const htmlToFormattedText = (element) => {
    if (!element) return '-';

    // Clone het element om het origineel niet te wijzigen
    const clone = element.cloneNode(true);

    // Verwijder afbeeldingen uit de tekst extractie
    clone.querySelectorAll('img').forEach(img => img.remove());

    // Functie om recursief door de DOM te lopen en tekst te extraheren
    const extractText = (node, isFirst = true) => {
      let result = '';

      node.childNodes.forEach((child, index) => {
        if (child.nodeType === Node.TEXT_NODE) {
          // Tekst node - voeg tekst toe
          const text = child.textContent;
          if (text.trim()) {
            result += text;
          }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const tagName = child.tagName.toLowerCase();

          if (tagName === 'br') {
            // BR tag - voeg regelafbreking toe
            result += '\n';
          } else if (tagName === 'li') {
            // List item - voeg bullet toe
            result += '• ' + extractText(child, false) + '\n';
          } else if (['div', 'p'].includes(tagName)) {
            // Block elementen - voeg tekst toe met regelafbreking erna
            const childText = extractText(child, false).trim();
            if (childText) {
              // Als er al tekst is en dit is niet het eerste element, voeg newline toe
              if (result.trim() && !result.endsWith('\n')) {
                result += '\n';
              }
              result += childText;
              // Voeg newline toe na block element
              if (!result.endsWith('\n')) {
                result += '\n';
              }
            }
          } else if (['span', 'strong', 'b', 'i', 'em', 'a'].includes(tagName)) {
            // Inline elementen - voeg tekst toe zonder regelafbreking
            result += extractText(child, false);
          } else {
            // Andere elementen - behandel als inline
            result += extractText(child, false);
          }
        }
      });

      return result;
    };

    let text = extractText(clone);

    // Vervang meerdere opeenvolgende newlines door maximaal 2
    text = text.replace(/\n{3,}/g, '\n\n');

    // Trim het eindresultaat
    text = text.trim();

    return text || '-';
  };

  /**
   * Render HTML chapter description naar PDF met behoud van opmaak
   */
  const renderChapterDescriptionToPdf = (pdf, descElement, startY, margin, maxWidth) => {
    if (!descElement) return startY;

    let currentY = startY + 2;
    const lineHeight = 4.5;
    const listIndent = 6;

    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text.trim()) {
          const lines = pdf.splitTextToSize(text, maxWidth);
          pdf.text(lines, margin, currentY);
          currentY += lines.length * lineHeight;
        }
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const tag = node.tagName.toLowerCase();

      if (tag === 'p') {
        // Render paragraph inhoud met inline formatting
        renderInlineContent(node, margin, maxWidth);
        currentY += lineHeight * 0.5; // Extra ruimte na paragraaf
      } else if (tag === 'ol' || tag === 'ul') {
        const items = node.querySelectorAll(':scope > li');
        items.forEach((li, index) => {
          const prefix = tag === 'ol' ? `${index + 1}. ` : '• ';
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.text(prefix, margin + 1, currentY);
          const prefixWidth = pdf.getTextWidth(prefix) + 1;
          renderInlineContent(li, margin + listIndent, maxWidth - listIndent);
          if (currentY < startY + 200) { // safety
            // currentY is al bijgewerkt door renderInlineContent
          }
        });
        currentY += lineHeight * 0.3;
      } else {
        // Andere block elementen: recursief verwerken
        Array.from(node.childNodes).forEach(child => processNode(child));
      }
    };

    const renderInlineContent = (element, x, availWidth) => {
      // Verzamel alle tekst-segmenten met hun stijl
      const segments = [];
      const collectSegments = (node, isBold, isUnderline) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          if (text) {
            segments.push({ text, bold: isBold, underline: isUnderline });
          }
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const tag = node.tagName.toLowerCase();
        const nowBold = isBold || tag === 'strong' || tag === 'b';
        const nowUnderline = isUnderline || tag === 'u';
        Array.from(node.childNodes).forEach(child => collectSegments(child, nowBold, nowUnderline));
      };
      collectSegments(element, false, false);

      // Render segmenten met word-wrap
      pdf.setFontSize(9);
      let lineX = x;
      const spaceWidth = (() => { pdf.setFont('helvetica', 'normal'); return pdf.getTextWidth(' '); })();

      segments.forEach(seg => {
        const words = seg.text.split(/(\s+)/);
        words.forEach(word => {
          if (!word) return;
          const fontStyle = seg.bold ? 'bold' : 'normal';
          pdf.setFont('helvetica', fontStyle);
          const wordWidth = pdf.getTextWidth(word);

          if (lineX + wordWidth > x + availWidth && lineX > x) {
            // Nieuwe regel
            currentY += lineHeight;
            lineX = x;
          }

          pdf.text(word, lineX, currentY);

          if (seg.underline) {
            const textY = currentY + 0.5;
            pdf.setDrawColor(100, 100, 100);
            pdf.setLineWidth(0.2);
            pdf.line(lineX, textY, lineX + wordWidth, textY);
          }

          lineX += wordWidth;
        });
      });

      currentY += lineHeight;
      // Reset lineX is niet nodig want volgende call start opnieuw
    };

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(80, 80, 80);

    Array.from(descElement.childNodes).forEach(child => processNode(child));

    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);

    return currentY + 2;
  };

  const extractTableData = table => {
    const headers = [];
    const body = [];
    const imageMap = new Map(); // Key: "rowIndex-colIndex", Value: image URL

    // Extract headers
    const thead = table.querySelector('thead');
    if (thead) {
      const headerRow = thead.querySelector('tr');
      if (headerRow) {
        headerRow.querySelectorAll('th').forEach(th => {
          headers.push(th.textContent.trim());
        });
      }
    }

    // Extract body rows
    const tbody = table.querySelector('tbody');
    if (tbody) {
      const rows = tbody.querySelectorAll('tr');
      rows.forEach((tr, rowIndex) => {
        const rowData = [];
        const cells = tr.querySelectorAll('td');
        cells.forEach((td, colIndex) => {
          // Check voor afbeeldingen
          const imgs = td.querySelectorAll('img');
          if (imgs.length > 0) {
            // Sla alle afbeelding URLs op
            const imgSrcs = Array.from(imgs).map(img => img.src).filter(Boolean);
            if (imgSrcs.length > 0) {
              imageMap.set(`${rowIndex}-${colIndex}`, imgSrcs);
            }
            // Laat cel leeg voor afbeeldingen
            rowData.push('');
          } else {
            // Neem tekst content met behoud van opmaak
            rowData.push(htmlToFormattedText(td));
          }
        });
        // Check of dit een deviation row is
        const isDeviation = tr.classList.contains('row-with-deviation');
        if (isDeviation) {
          rowData._isDeviation = true;
        }
        body.push(rowData);
      });
    }

    return {headers, body, imageMap};
  };

  /**
   * Voer async taken uit in batches om rate limiting te voorkomen
   */
  const runInBatches = async (tasks, batchSize = 5) => {
    const results = [];
    for (let i = 0; i < tasks.length; i += batchSize) {
      const batch = tasks.slice(i, i + batchSize);
      const batchResults = await Promise.all(batch.map(fn => fn()));
      results.push(...batchResults);
    }
    return results;
  };

  /**
   * Pre-load alle afbeeldingen in een element
   */
  const preloadImages = async (element) => {
    const imgs = Array.from(element.querySelectorAll('img'));
    const toLoad = imgs.filter(img => !(img.complete && img.naturalWidth > 0));
    if (toLoad.length === 0) return;

    const tasks = toLoad.map(img => () => new Promise((resolve) => {
      const newImg = new Image();
      newImg.crossOrigin = 'anonymous';
      newImg.onload = () => {
        img.src = newImg.src;
        resolve();
      };
      newImg.onerror = () => {
        console.warn('Preload failed for:', img.src);
        resolve();
      };
      newImg.src = img.src;
    }));
    await runInBatches(tasks, 3);
  };

  /**
   * Laad een afbeelding en converteer naar base64 data URL
   */
  const loadImageAsBase64 = async (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const maxSize = 200; // Max breedte/hoogte in pixels voor PDF
          let width = img.width;
          let height = img.height;

          // Schaal naar max grootte met behoud van aspect ratio
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve({
            dataUrl: canvas.toDataURL('image/jpeg', 0.85),
            width,
            height
          });
        } catch (e) {
          console.warn('Failed to convert image to base64:', e);
          reject(e);
        }
      };
      img.onerror = (e) => {
        console.warn('Failed to load image:', url, e);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  };

  /**
   * Render een DOM element naar canvas met html2canvas
   */
  const renderPageToCanvas = async (element) => {
    return html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
    });
  };

  /**
   * Voeg een canvas als image toe aan de PDF, splits over meerdere pagina's indien nodig
   */
  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;

    setDownloadingPDF(true);
    try {
      const reportElement = reportRef.current;

      // Pre-load alle afbeeldingen
      await preloadImages(reportElement);

      reportElement.classList.add('pdf-rendering');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let isFirstPage = true;

      // Stap 1: Pre-render alle niet-tabel pagina's parallel met html2canvas
      const pages = Array.from(reportElement.querySelectorAll('.report-page'));
      const pageData = [];

      // Categoriseer pagina's en start html2canvas parallel voor niet-tabel pagina's
      const canvasPromises = new Map();
      const hiddenTitles = new Map();
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const allTables = [];
        const mainTable = page.querySelector('.findings-table') ||
                          page.querySelector('.documentation-table') ||
                          page.querySelector('table.findings-table');
        const schaaldelenTables = page.querySelectorAll('.schaaldelen-overview table');
        schaaldelenTables.forEach(t => allTables.push(t));
        if (allTables.length === 0 && mainTable) allTables.push(mainTable);
        if (allTables.length === 0) {
          const fallbackTable = page.querySelector('table');
          if (fallbackTable) allTables.push(fallbackTable);
        }
        const table = allTables[0];
        const hasTable = table && table.querySelector('tbody tr');

        if (hasTable) {
          pageData.push({type: 'table', page, table, mainTable});
        } else {
          // Verberg section-title voor canvas rendering zodat we die native kunnen renderen
          const sectionTitle = page.querySelector('.section-title');
          if (sectionTitle) {
            hiddenTitles.set(i, {element: sectionTitle, text: sectionTitle.textContent.trim()});
            sectionTitle.style.display = 'none';
          }
          // Start html2canvas direct (parallel)
          canvasPromises.set(i, renderPageToCanvas(page));
          pageData.push({type: 'canvas', page, canvasIndex: i});
        }
      }

      // Wacht tot alle canvas renders klaar zijn en herstel verborgen titels
      const canvasResults = new Map();
      for (const [idx, promise] of canvasPromises) {
        canvasResults.set(idx, await promise);
      }
      for (const [idx, {element}] of hiddenTitles) {
        element.style.display = '';
      }

      // Stap 2: Bouw de PDF op in volgorde
      for (const item of pageData) {
        if (item.type === 'canvas') {
          const canvas = canvasResults.get(item.canvasIndex);
          if (!canvas) continue;
          const imgWidth = pdfWidth - 2 * margin;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          if (imgHeight < 20) continue;

          if (!isFirstPage) pdf.addPage();
          isFirstPage = false;

          // Render section-title native met jsPDF voor consistente blauwe titels
          const titleInfo = hiddenTitles.get(item.canvasIndex);
          let canvasStartY = margin;
          if (titleInfo && titleInfo.text) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 102, 204);
            pdf.text(titleInfo.text, margin, canvasStartY + 5);
            canvasStartY += 12;
          }

          // Render de rest van de pagina als canvas
          const usableHeight = pdfHeight - margin - canvasStartY;
          if (imgHeight > usableHeight) {
            const numPages = Math.ceil(imgHeight / usableHeight);
            for (let pageNum = 0; pageNum < numPages; pageNum++) {
              if (pageNum > 0) {
                pdf.addPage();
                canvasStartY = margin;
              }
              const currentUsableHeight = pdfHeight - margin - canvasStartY;
              const sourceY = pageNum === 0 ? 0 : ((margin + (pdfHeight - 2 * margin) * (pageNum - 1) + usableHeight) * canvas.width) / imgWidth;
              const sourceHeight = Math.min(
                (currentUsableHeight * canvas.width) / imgWidth,
                canvas.height - sourceY,
              );
              if (sourceHeight <= 0) break;
              const pageCanvas = document.createElement('canvas');
              const ctx = pageCanvas.getContext('2d');
              pageCanvas.width = canvas.width;
              pageCanvas.height = sourceHeight;
              ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
              const pageImgHeight = (sourceHeight * imgWidth) / canvas.width;
              pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, canvasStartY, imgWidth, pageImgHeight);
            }
          } else {
            pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, canvasStartY, imgWidth, imgHeight);
          }
        } else {
          // Tabel pagina
          if (!isFirstPage) pdf.addPage();
          isFirstPage = false;

          // Header direct met jsPDF
          const sectionTitle = item.page.querySelector('.section-title');
          const chapterDescription = item.page.querySelector('.chapter-description');
          const categoryTitle = item.page.querySelector('.category-title');
          let currentY = margin;

          const titleText = sectionTitle?.textContent?.trim() || categoryTitle?.textContent?.trim();
          if (titleText) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 102, 204);
            pdf.text(titleText, margin, currentY + 5);
            currentY += 12;
          }

          if (chapterDescription && chapterDescription.textContent.trim()) {
            currentY = renderChapterDescriptionToPdf(pdf, chapterDescription, currentY, margin, pdfWidth - 2 * margin);
          }

          // AutoTable
          const {headers, body, imageMap} = extractTableData(item.table);

          const loadedImages = new Map();
          const imageLoadTasks = [];
          for (const [key, urls] of imageMap.entries()) {
            const urlArray = Array.isArray(urls) ? urls : [urls];
            urlArray.forEach((url, imgIndex) => {
              imageLoadTasks.push(async () => {
                try {
                  const imageData = await loadImageAsBase64(url);
                  if (!loadedImages.has(key)) loadedImages.set(key, []);
                  loadedImages.get(key)[imgIndex] = imageData;
                } catch (e) {
                  console.warn(`Failed to load image for ${key}[${imgIndex}]:`, e);
                }
              });
            });
          }
          await runInBatches(imageLoadTasks, 5);

          const availableWidth = pdfWidth - 2 * margin;
          const isSchaaldelen = headers.length === 4 && headers[0] === 'Nr.';
          const hasPhotoColumn = headers.length === 3;
          let columnStyles;
          if (isSchaaldelen) {
            columnStyles = {
              0: {cellWidth: availableWidth * 0.10},
              1: {cellWidth: availableWidth * 0.20},
              2: {cellWidth: availableWidth * 0.20},
              3: {cellWidth: availableWidth * 0.50, minCellHeight: 30},
            };
          } else if (hasPhotoColumn) {
            columnStyles = {
              0: {cellWidth: availableWidth * 0.35},
              1: {cellWidth: availableWidth * 0.40},
              2: {cellWidth: availableWidth * 0.25, minCellHeight: 40},
            };
          } else {
            columnStyles = {
              0: {cellWidth: availableWidth * 0.45},
              1: {cellWidth: availableWidth * 0.55},
            };
          }

          autoTable(pdf, {
            startY: currentY,
            head: [headers],
            body: body,
            theme: 'grid',
            headStyles: {
              fillColor: [0, 102, 204],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              fontSize: 10,
              cellPadding: 4,
            },
            bodyStyles: {
              fontSize: 9,
              cellPadding: 4,
              lineColor: [221, 221, 221],
              lineWidth: 0.1,
            },
            alternateRowStyles: {
              fillColor: [248, 249, 250],
            },
            columnStyles,
            margin: {left: margin, right: margin},
            tableWidth: availableWidth,
            showHead: 'everyPage',
            rowPageBreak: 'avoid',
            didParseCell: function (data) {
              if (data.row.raw && data.row.raw._isDeviation) {
                data.cell.styles.fillColor = [255, 254, 245];
              }
              if (data.section === 'body') {
                const imageKey = `${data.row.index}-${data.column.index}`;
                const images = loadedImages.get(imageKey);
                if (images) {
                  const count = images.filter(Boolean).length;
                  if (count > 0) {
                    data.cell.styles.minCellHeight = count * 32 + (count - 1) * 2 + 4;
                  }
                }
              }
            },
            willDrawCell: function (data) {
              if (data.row.raw && data.row.raw._isDeviation && data.column.index === 0) {
                pdf.setDrawColor(255, 193, 7);
                pdf.setLineWidth(1);
                pdf.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height);
              }
            },
            didDrawCell: function (data) {
              if (data.section === 'body') {
                const imageKey = `${data.row.index}-${data.column.index}`;
                const images = loadedImages.get(imageKey);
                if (images && images.length > 0) {
                  const cellPadding = 2;
                  const maxWidth = data.cell.width - 2 * cellPadding;
                  const gap = 2;
                  const validImages = images.filter(Boolean);
                  const perImgMaxHeight = (data.cell.height - 2 * cellPadding - (validImages.length - 1) * gap) / validImages.length;
                  let offsetY = data.cell.y + cellPadding;

                  validImages.forEach((imageData) => {
                    const imgWidthMm = imageData.width * 0.26;
                    const imgHeightMm = imageData.height * 0.26;
                    let finalWidth = imgWidthMm;
                    let finalHeight = imgHeightMm;
                    if (finalWidth > maxWidth) {
                      const ratio = maxWidth / finalWidth;
                      finalWidth = maxWidth;
                      finalHeight *= ratio;
                    }
                    if (finalHeight > perImgMaxHeight) {
                      const ratio = perImgMaxHeight / finalHeight;
                      finalHeight = perImgMaxHeight;
                      finalWidth *= ratio;
                    }
                    const x = data.cell.x + (data.cell.width - finalWidth) / 2;
                    const y = offsetY;
                    try {
                      pdf.addImage(imageData.dataUrl, 'JPEG', x, y, finalWidth, finalHeight);
                    } catch (e) {
                      console.warn('Failed to add image to PDF:', e);
                    }
                    offsetY += finalHeight + gap;
                  });
                }
              }
            },
          });

          // Als we een schaaldelen tabel renderde, check of er ook reguliere rijen in de findings-table staan (bijv. Conclusie)
          if (isSchaaldelen && item.mainTable && item.mainTable !== item.table) {
            const mainTbody = item.mainTable.querySelector('tbody');
            if (mainTbody) {
              // Filter rijen die GEEN schaaldelen of inspectieonderdelen containers zijn
              const regularRows = Array.from(mainTbody.querySelectorAll(':scope > tr')).filter(tr => {
                return !tr.querySelector('.schaaldelen-overview') && !tr.querySelector('table');
              });

              if (regularRows.length > 0) {
                // Extract headers van de findings-table
                const mainHeaders = [];
                const mainThead = item.mainTable.querySelector('thead');
                if (mainThead) {
                  mainThead.querySelectorAll('th').forEach(th => mainHeaders.push(th.textContent.trim()));
                }

                // Extract data uit de reguliere rijen
                const mainBody = [];
                const mainImageMap = new Map();
                regularRows.forEach((tr, rowIndex) => {
                  const rowData = [];
                  const cells = tr.querySelectorAll('td');
                  cells.forEach((td, colIndex) => {
                    const imgs = td.querySelectorAll('img');
                    if (imgs.length > 0) {
                      const imgSrcs = Array.from(imgs).map(img => img.src).filter(Boolean);
                      if (imgSrcs.length > 0) {
                        mainImageMap.set(`${rowIndex}-${colIndex}`, imgSrcs);
                      }
                      rowData.push('');
                    } else {
                      rowData.push(htmlToFormattedText(td));
                    }
                  });
                  if (tr.classList.contains('row-with-deviation')) {
                    rowData._isDeviation = true;
                  }
                  mainBody.push(rowData);
                });

                if (mainBody.length > 0 && mainHeaders.length > 0) {
                  // Laad afbeeldingen voor reguliere rijen
                  const mainLoadedImages = new Map();
                  const mainImageTasks = [];
                  for (const [key, urls] of mainImageMap.entries()) {
                    const urlArray = Array.isArray(urls) ? urls : [urls];
                    urlArray.forEach((url, imgIndex) => {
                      mainImageTasks.push(async () => {
                        try {
                          const imageData = await loadImageAsBase64(url);
                          if (!mainLoadedImages.has(key)) mainLoadedImages.set(key, []);
                          mainLoadedImages.get(key)[imgIndex] = imageData;
                        } catch (e) {
                          console.warn(`Failed to load image for regular row ${key}[${imgIndex}]:`, e);
                        }
                      });
                    });
                  }
                  await runInBatches(mainImageTasks, 3);

                  // Bepaal column styles voor reguliere findings-tabel
                  const mainHasPhotoColumn = mainHeaders.length === 3;
                  let mainColumnStyles;
                  if (mainHasPhotoColumn) {
                    mainColumnStyles = {
                      0: {cellWidth: availableWidth * 0.35},
                      1: {cellWidth: availableWidth * 0.40},
                      2: {cellWidth: availableWidth * 0.25, minCellHeight: 40},
                    };
                  } else {
                    mainColumnStyles = {
                      0: {cellWidth: availableWidth * 0.45},
                      1: {cellWidth: availableWidth * 0.55},
                    };
                  }

                  const afterSchaaldelenY = pdf.lastAutoTable.finalY + 5;
                  autoTable(pdf, {
                    startY: afterSchaaldelenY,
                    head: [mainHeaders],
                    body: mainBody,
                    theme: 'grid',
                    headStyles: {
                      fillColor: [0, 102, 204],
                      textColor: [255, 255, 255],
                      fontStyle: 'bold',
                      fontSize: 10,
                      cellPadding: 4,
                    },
                    bodyStyles: {
                      fontSize: 9,
                      cellPadding: 4,
                      lineColor: [221, 221, 221],
                      lineWidth: 0.1,
                    },
                    alternateRowStyles: {
                      fillColor: [248, 249, 250],
                    },
                    columnStyles: mainColumnStyles,
                    margin: {left: margin, right: margin},
                    tableWidth: availableWidth,
                    showHead: 'everyPage',
                    rowPageBreak: 'avoid',
                    didParseCell: function (data) {
                      if (data.row.raw && data.row.raw._isDeviation) {
                        data.cell.styles.fillColor = [255, 254, 245];
                      }
                      if (data.section === 'body') {
                        const imageKey = `${data.row.index}-${data.column.index}`;
                        const images = mainLoadedImages.get(imageKey);
                        if (images) {
                          const count = images.filter(Boolean).length;
                          if (count > 0) {
                            data.cell.styles.minCellHeight = count * 32 + (count - 1) * 2 + 4;
                          }
                        }
                      }
                    },
                    willDrawCell: function (data) {
                      if (data.row.raw && data.row.raw._isDeviation && data.column.index === 0) {
                        pdf.setDrawColor(255, 193, 7);
                        pdf.setLineWidth(1);
                        pdf.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height);
                      }
                    },
                    didDrawCell: function (data) {
                      if (data.section === 'body') {
                        const imageKey = `${data.row.index}-${data.column.index}`;
                        const images = mainLoadedImages.get(imageKey);
                        if (images && images.length > 0) {
                          const cellPadding = 2;
                          const maxWidth = data.cell.width - 2 * cellPadding;
                          const gap = 2;
                          const validImages = images.filter(Boolean);
                          const perImgMaxHeight = (data.cell.height - 2 * cellPadding - (validImages.length - 1) * gap) / validImages.length;
                          let offsetY = data.cell.y + cellPadding;

                          validImages.forEach((imageData) => {
                            const imgWidthMm = imageData.width * 0.26;
                            const imgHeightMm = imageData.height * 0.26;
                            let finalWidth = imgWidthMm;
                            let finalHeight = imgHeightMm;
                            if (finalWidth > maxWidth) {
                              const ratio = maxWidth / finalWidth;
                              finalWidth = maxWidth;
                              finalHeight *= ratio;
                            }
                            if (finalHeight > perImgMaxHeight) {
                              const ratio = perImgMaxHeight / finalHeight;
                              finalHeight = perImgMaxHeight;
                              finalWidth *= ratio;
                            }
                            const x = data.cell.x + (data.cell.width - finalWidth) / 2;
                            const y = offsetY;
                            try {
                              pdf.addImage(imageData.dataUrl, 'JPEG', x, y, finalWidth, finalHeight);
                            } catch (e) {
                              console.warn('Failed to add image to PDF:', e);
                            }
                            offsetY += finalHeight + gap;
                          });
                        }
                      }
                    },
                  });
                }
              }
            }
          }
        }
      }

      reportElement.classList.remove('pdf-rendering');

      const company = reportData?.inspection?.company_id?.name || 'Onbekend';
      const template = reportData?.inspection?.template_id?.name || '';
      const date = reportData?.inspection?.inspection_date
        ? new Date(reportData.inspection.inspection_date).toLocaleDateString('nl-NL')
        : new Date().toLocaleDateString('nl-NL');
      const filename = `Inspectie_${company}${template ? `_${template}` : ''}_${date}.pdf`.replace(/\s+/g, '_');

      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Fout bij het genereren van de PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Email functies
  const openEmailModal = async () => {
    setLoadingTemplates(true);
    setShowEmailModal(true);
    setPublicReportUrl(null);

    try {
      // Genereer publieke link
      const tokenResponse = await dispatch(generatePublicToken(id));
      if (tokenResponse?.result?.public_url) {
        setPublicReportUrl(tokenResponse.result.public_url);
      }

      const response = await dispatch(getEmailTemplates());
      const templates = response.results || [];
      setEmailTemplates(templates);

      // Selecteer standaard template of eerste template
      const defaultTemplate = templates.find(t => t.is_default) || templates[0];
      if (defaultTemplate) {
        selectEmailTemplate(defaultTemplate);
      } else {
        // Geen templates, gebruik default waarden
        setEmailData({
          to: reportData?.inspection?.company_id?.contact_email || '',
          cc: '',
          subject: `Inspectierapport ${
            reportData?.inspection?.company_id?.name || ''
          } - ${formatDate(reportData?.inspection?.inspection_date)}`,
          body: `Geachte heer/mevrouw,\n\nHierbij ontvangt u het inspectierapport.\n\nMet vriendelijke groet,\n\nZwembadinspecties.nl`,
        });
      }
    } catch (error) {
      console.error('Error loading email templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const selectEmailTemplate = template => {
    setSelectedTemplate(template);

    // Vervang variabelen in subject en body
    const inspection = reportData?.inspection;
    const variables = {
      '{{zwembadnaam}}': inspection?.company_id?.name || '',
      '{{bedrijfsnaam}}': inspection?.company_id?.name || '',
      '{{inspectiedatum}}': formatDate(inspection?.inspection_date),
      '{{inspecteur}}': `${inspection?.inspector_id?.voornaam || ''} ${
        inspection?.inspector_id?.achternaam || ''
      }`.trim(),
      '{{rapportnummer}}': `ZB${new Date().getFullYear()}-${
        inspection?._id?.slice(-8) || ''
      }`,
      '{{template}}': inspection?.template_id?.name || '',
      '{{categorie}}': inspection?.template_id?.category || '',
    };

    let subject = template.subject;
    let body = template.body;

    Object.keys(variables).forEach(key => {
      subject = subject.replace(new RegExp(key, 'g'), variables[key]);
      body = body.replace(new RegExp(key, 'g'), variables[key]);
    });

    setEmailData({
      to: reportData?.inspection?.company_id?.contact_email || '',
      cc: '',
      subject,
      body,
    });
  };

  const handleSendEmail = async () => {
    if (!emailData.to || !emailData.subject || !emailData.body) {
      alert('Vul alle verplichte velden in');
      return;
    }

    // Valideer email formaat
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailData.to)) {
      alert('Voer een geldig e-mailadres in');
      return;
    }

    setSendingEmail(true);

    try {
      // Genereer PDF
      const reportElement = reportRef.current;
      if (!reportElement) {
        console.error('Report element not found');
        alert('Fout: Rapport element niet gevonden');
        setSendingEmail(false);
        return;
      }

      // Pre-load alle afbeeldingen voordat we beginnen met renderen
      await preloadImages(reportElement);

      reportElement.classList.add('pdf-rendering');

      const pages = reportElement.querySelectorAll('.report-page');

      if (pages.length === 0) {
        alert("Fout: Geen rapport pagina's gevonden");
        reportElement.classList.remove('pdf-rendering');
        setSendingEmail(false);
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const usableHeight = pdfHeight - 2 * margin;
      let isFirstPage = true;

      for (const page of pages) {
        // Zoek naar alle tabellen die we met autoTable willen renderen
        // Verzamel alle tabellen op deze pagina (inclusief schaaldelen en geneste tabellen)
        const allTables = [];

        // Hoofdtabel (findings-table of andere)
        const mainTable = page.querySelector('.findings-table') ||
                          page.querySelector('.documentation-table') ||
                          page.querySelector('table.findings-table');

        // Schaaldelen tabellen (kunnen genest zijn)
        const schaaldelenTables = page.querySelectorAll('.schaaldelen-overview table');

        // Voeg schaaldelen tabellen toe aan de lijst
        schaaldelenTables.forEach(t => allTables.push(t));

        // Als er geen schaaldelen zijn, gebruik de hoofdtabel
        if (allTables.length === 0 && mainTable) {
          allTables.push(mainTable);
        }

        // Fallback naar eerste tabel op de pagina
        if (allTables.length === 0) {
          const fallbackTable = page.querySelector('table');
          if (fallbackTable) allTables.push(fallbackTable);
        }

        // Gebruik de eerste gevonden tabel voor autoTable rendering
        const table = allTables[0];

        if (table && table.querySelector('tbody tr')) {
          // Pagina met tabel - gebruik autoTable voor betere paginering
          const tbody = table.querySelector('tbody');
          const rows = tbody ? Array.from(tbody.querySelectorAll('tr')) : [];

          if (!isFirstPage) pdf.addPage();
          isFirstPage = false;

          // Render chapter header direct met jsPDF
          const sectionTitle = page.querySelector('.section-title');
          const chapterDescription = page.querySelector('.chapter-description');
          const categoryTitle = page.querySelector('.category-title');

          let currentY = margin;

          const titleText = sectionTitle?.textContent?.trim() || categoryTitle?.textContent?.trim();
          if (titleText) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 102, 204);
            pdf.text(titleText, margin, currentY + 5);
            currentY += 12;
          }

          if (chapterDescription && chapterDescription.textContent.trim()) {
            currentY = renderChapterDescriptionToPdf(pdf, chapterDescription, currentY, margin, pdfWidth - 2 * margin);
          }

          // Gebruik autoTable voor de tabel data
          if (rows.length > 0) {
            const {headers, body, imageMap} = extractTableData(table);

            // Pre-load alle afbeeldingen in batches om rate limiting te voorkomen
            const loadedImages = new Map();
            const imageLoadTasks = [];
            for (const [key, urls] of imageMap.entries()) {
              const urlArray = Array.isArray(urls) ? urls : [urls];
              urlArray.forEach((url, imgIndex) => {
                imageLoadTasks.push(async () => {
                  try {
                    const imageData = await loadImageAsBase64(url);
                    if (!loadedImages.has(key)) loadedImages.set(key, []);
                    loadedImages.get(key)[imgIndex] = imageData;
                  } catch (e) {
                    console.warn(`Failed to load image for ${key}[${imgIndex}]:`, e);
                  }
                });
              });
            }
            await runInBatches(imageLoadTasks, 3);

            const availableWidth = pdfWidth - 2 * margin;
            const isSchaaldelen = headers.length === 4 && headers[0] === 'Nr.';
            const hasPhotoColumn = headers.length === 3;
            let columnStyles;
            if (isSchaaldelen) {
              // Schaaldelen tabel: Nr., Naad, Deel, Foto's
              columnStyles = {
                0: {cellWidth: availableWidth * 0.10}, // Nr.
                1: {cellWidth: availableWidth * 0.20}, // Naad
                2: {cellWidth: availableWidth * 0.20}, // Deel
                3: {cellWidth: availableWidth * 0.50, minCellHeight: 30}, // Foto's
              };
            } else if (hasPhotoColumn) {
              columnStyles = {
                0: {cellWidth: availableWidth * 0.35},
                1: {cellWidth: availableWidth * 0.40},
                2: {cellWidth: availableWidth * 0.25, minCellHeight: 40},
              };
            } else {
              columnStyles = {
                0: {cellWidth: availableWidth * 0.45},
                1: {cellWidth: availableWidth * 0.55},
              };
            }

            const renderAutoTable = (pdfDoc, tableHeaders, tableBody, tableLoadedImages, tableColumnStyles, startY) => {
              autoTable(pdfDoc, {
                startY: startY,
                head: [tableHeaders],
                body: tableBody,
                theme: 'grid',
                headStyles: {
                  fillColor: [0, 102, 204],
                  textColor: [255, 255, 255],
                  fontStyle: 'bold',
                  fontSize: 10,
                  cellPadding: 4,
                },
                bodyStyles: {
                  fontSize: 9,
                  cellPadding: 4,
                  lineColor: [221, 221, 221],
                  lineWidth: 0.1,
                },
                alternateRowStyles: {
                  fillColor: [248, 249, 250],
                },
                columnStyles: tableColumnStyles,
                margin: {left: margin, right: margin},
                tableWidth: pdfWidth - 2 * margin,
                showHead: 'everyPage',
                rowPageBreak: 'avoid',
                didParseCell: function (data) {
                  if (data.row.raw && data.row.raw._isDeviation) {
                    data.cell.styles.fillColor = [255, 254, 245];
                  }
                  if (data.section === 'body') {
                    const imageKey = `${data.row.index}-${data.column.index}`;
                    const images = tableLoadedImages.get(imageKey);
                    if (images) {
                      const count = images.filter(Boolean).length;
                      if (count > 0) {
                        data.cell.styles.minCellHeight = count * 32 + (count - 1) * 2 + 4;
                      }
                    }
                  }
                },
                willDrawCell: function (data) {
                  if (data.row.raw && data.row.raw._isDeviation && data.column.index === 0) {
                    pdf.setDrawColor(255, 193, 7);
                    pdf.setLineWidth(1);
                    pdf.line(
                      data.cell.x,
                      data.cell.y,
                      data.cell.x,
                      data.cell.y + data.cell.height,
                    );
                  }
                },
                didDrawCell: function (data) {
                  if (data.section === 'body') {
                    const imageKey = `${data.row.index}-${data.column.index}`;
                    const images = tableLoadedImages.get(imageKey);
                    if (images && images.length > 0) {
                      const cellPadding = 2;
                      const maxWidth = data.cell.width - 2 * cellPadding;
                      const gap = 2;
                      const validImages = images.filter(Boolean);
                      const perImgMaxHeight = (data.cell.height - 2 * cellPadding - (validImages.length - 1) * gap) / validImages.length;
                      let offsetY = data.cell.y + cellPadding;

                      validImages.forEach((imageData) => {
                        const imgWidthMm = imageData.width * 0.26;
                        const imgHeightMm = imageData.height * 0.26;
                        let finalWidth = imgWidthMm;
                        let finalHeight = imgHeightMm;
                        if (finalWidth > maxWidth) {
                          const ratio = maxWidth / finalWidth;
                          finalWidth = maxWidth;
                          finalHeight *= ratio;
                        }
                        if (finalHeight > perImgMaxHeight) {
                          const ratio = perImgMaxHeight / finalHeight;
                          finalHeight = perImgMaxHeight;
                          finalWidth *= ratio;
                        }
                        const x = data.cell.x + (data.cell.width - finalWidth) / 2;
                        const y = offsetY;
                        try {
                          pdf.addImage(
                            imageData.dataUrl,
                            'JPEG',
                            x,
                            y,
                            finalWidth,
                            finalHeight
                          );
                        } catch (e) {
                          console.warn('Failed to add image to PDF:', e);
                        }
                        offsetY += finalHeight + gap;
                      });
                    }
                  }
                },
              });
            };

            renderAutoTable(pdf, headers, body, loadedImages, columnStyles, currentY);

            // Als we een schaaldelen tabel renderde, check of er ook reguliere rijen in de findings-table staan (bijv. Conclusie)
            if (isSchaaldelen && mainTable && mainTable !== table) {
              const mainTbody = mainTable.querySelector('tbody');
              if (mainTbody) {
                // Filter rijen die GEEN schaaldelen of inspectieonderdelen containers zijn
                const regularRows = Array.from(mainTbody.querySelectorAll(':scope > tr')).filter(tr => {
                  return !tr.querySelector('.schaaldelen-overview') && !tr.querySelector('table');
                });

                if (regularRows.length > 0) {
                  // Extract headers van de findings-table
                  const mainHeaders = [];
                  const mainThead = mainTable.querySelector('thead');
                  if (mainThead) {
                    mainThead.querySelectorAll('th').forEach(th => mainHeaders.push(th.textContent.trim()));
                  }

                  // Extract data uit de reguliere rijen
                  const mainBody = [];
                  const mainImageMap = new Map();
                  regularRows.forEach((tr, rowIndex) => {
                    const rowData = [];
                    const cells = tr.querySelectorAll('td');
                    cells.forEach((td, colIndex) => {
                      const imgs = td.querySelectorAll('img');
                      if (imgs.length > 0) {
                        const imgSrcs = Array.from(imgs).map(img => img.src).filter(Boolean);
                        if (imgSrcs.length > 0) {
                          mainImageMap.set(`${rowIndex}-${colIndex}`, imgSrcs);
                        }
                        rowData.push('');
                      } else {
                        rowData.push(htmlToFormattedText(td));
                      }
                    });
                    if (tr.classList.contains('row-with-deviation')) {
                      rowData._isDeviation = true;
                    }
                    mainBody.push(rowData);
                  });

                  if (mainBody.length > 0 && mainHeaders.length > 0) {
                    // Laad afbeeldingen voor reguliere rijen
                    const mainLoadedImages = new Map();
                    const mainImageTasks = [];
                    for (const [key, urls] of mainImageMap.entries()) {
                      const urlArray = Array.isArray(urls) ? urls : [urls];
                      urlArray.forEach((url, imgIndex) => {
                        mainImageTasks.push(async () => {
                          try {
                            const imageData = await loadImageAsBase64(url);
                            if (!mainLoadedImages.has(key)) mainLoadedImages.set(key, []);
                            mainLoadedImages.get(key)[imgIndex] = imageData;
                          } catch (e) {
                            console.warn(`Failed to load image for regular row ${key}[${imgIndex}]:`, e);
                          }
                        });
                      });
                    }
                    await runInBatches(mainImageTasks, 3);

                    // Bepaal column styles voor reguliere findings-tabel
                    const mainHasPhotoColumn = mainHeaders.length === 3;
                    let mainColumnStyles;
                    if (mainHasPhotoColumn) {
                      mainColumnStyles = {
                        0: {cellWidth: availableWidth * 0.35},
                        1: {cellWidth: availableWidth * 0.40},
                        2: {cellWidth: availableWidth * 0.25, minCellHeight: 40},
                      };
                    } else {
                      mainColumnStyles = {
                        0: {cellWidth: availableWidth * 0.45},
                        1: {cellWidth: availableWidth * 0.55},
                      };
                    }

                    const afterSchaaldelenY = pdf.lastAutoTable.finalY + 5;
                    renderAutoTable(pdf, mainHeaders, mainBody, mainLoadedImages, mainColumnStyles, afterSchaaldelenY);
                  }
                }
              }
            }
          }
        } else if (page.querySelector('.section-title') && page.querySelector('.section-title').textContent.includes('Conclusie')) {
          // Conclusie pagina - render native met jsPDF voor betere kwaliteit
          if (!isFirstPage) pdf.addPage();
          isFirstPage = false;

          let currentY = margin;

          // Titel
          const sectionTitle = page.querySelector('.section-title');
          if (sectionTitle) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 102, 204);
            pdf.text(sectionTitle.textContent.trim(), margin, currentY + 5);
            currentY += 14;
          }

          // Conclusie tekst op basis van geselecteerde conclusies
          const inspectionDateStr = reportData?.inspection?.inspection_date
            ? new Date(reportData.inspection.inspection_date).toLocaleDateString('nl-NL', {day: '2-digit', month: '2-digit', year: 'numeric'})
            : '-';

          const selectedConclusieObjects = getSelectedConclusieObjects();
          const hasAnyDeviations = reportData.results?.some(
            r => r.has_deviation,
          );

          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(51, 51, 51);

          if (selectedConclusieObjects.length > 0) {
            // Toon geselecteerde conclusies
            selectedConclusieObjects.forEach(conclusie => {
              const conclusionText = (conclusie.text || '').replace(/\{\{inspectiedatum\}\}/g, inspectionDateStr);
              const conclusionLines = pdf.splitTextToSize(conclusionText, pdfWidth - 2 * margin);

              // Check of we nog op de pagina passen
              if (currentY + conclusionLines.length * 6 > pdfHeight - margin) {
                pdf.addPage();
                currentY = margin;
              }

              pdf.text(conclusionLines, margin, currentY);
              currentY += conclusionLines.length * 6 + 4;
            });

          } else {
            const noConclusion = 'Er zijn nog geen conclusies geselecteerd voor dit rapport.';
            const noLines = pdf.splitTextToSize(noConclusion, pdfWidth - 2 * margin);
            pdf.text(noLines, margin, currentY);
            currentY += noLines.length * 6 + 8;
          }

          // Toon afwijkingen samenvatting per hoofdstuk
          if (hasAnyDeviations && reportData.results) {
            const deviationResults = reportData.results.filter(r => r.has_deviation);

            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(51, 51, 51);
            pdf.text('Overzicht afwijkingen:', margin, currentY);
            currentY += 8;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');

            deviationResults.forEach(result => {
              const itemName = result.template_item_id?.question || 'Onbekend item';
              let severityText = '';
              if (result.deviation_severity === 'high') severityText = 'Hoog';
              else if (result.deviation_severity === 'medium') severityText = 'Gemiddeld';
              else severityText = 'Laag';

              // Check of we nog op de pagina passen
              if (currentY > pdfHeight - margin - 10) {
                pdf.addPage();
                currentY = margin;
              }

              // Bullet point met item naam en severity
              pdf.setTextColor(51, 51, 51);
              const bulletText = `• ${itemName}`;
              const bulletLines = pdf.splitTextToSize(bulletText, pdfWidth - 2 * margin - 5);
              pdf.text(bulletLines, margin + 3, currentY);
              currentY += bulletLines.length * 5;

              // Severity badge
              const severityColors = {
                high: [220, 53, 69],
                medium: [255, 193, 7],
                low: [40, 167, 69]
              };
              const color = severityColors[result.deviation_severity] || severityColors.low;
              pdf.setTextColor(color[0], color[1], color[2]);
              pdf.setFont('helvetica', 'bold');
              pdf.text(`  Ernst: ${severityText}`, margin + 5, currentY);
              pdf.setFont('helvetica', 'normal');
              currentY += 7;

              // Remarks als die er zijn
              if (result.remarks) {
                pdf.setTextColor(100, 100, 100);
                pdf.setFontSize(9);
                const remarkLines = pdf.splitTextToSize(result.remarks, pdfWidth - 2 * margin - 10);
                // Max 3 regels tonen
                const limitedLines = remarkLines.slice(0, 3);
                pdf.text(limitedLines, margin + 8, currentY);
                currentY += limitedLines.length * 4.5 + 3;
                pdf.setFontSize(10);
              }
            });
            currentY += 5;
          }

          // Algemene opmerkingen
          if (inspection.general_remarks) {
            if (currentY > pdfHeight - margin - 30) {
              pdf.addPage();
              currentY = margin;
            }

            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(51, 51, 51);
            pdf.text('Algemene Opmerkingen', margin, currentY);
            currentY += 7;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            const remarkLines = pdf.splitTextToSize(inspection.general_remarks, pdfWidth - 2 * margin);
            pdf.text(remarkLines, margin, currentY);
            currentY += remarkLines.length * 5 + 8;
          }

          // Datum van afronding
          if (inspection.end_time) {
            if (currentY > pdfHeight - margin - 20) {
              pdf.addPage();
              currentY = margin;
            }

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(100, 100, 100);
            pdf.text(`Inspectie afgerond op: ${formatDate(inspection.end_time)}`, margin, currentY);
            currentY += 6;

            if (inspection.signature_url) {
              pdf.text('Inspectie is digitaal ondertekend.', margin, currentY);
            }
          }
        } else {
          // Geen tabel - render titel native en rest met html2canvas
          const sectionTitle = page.querySelector('.section-title');
          const titleText = sectionTitle?.textContent?.trim();

          // Verberg titel voor canvas rendering
          if (sectionTitle) sectionTitle.style.display = 'none';

          const canvas = await html2canvas(page, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
          });

          // Herstel titel
          if (sectionTitle) sectionTitle.style.display = '';

          const imgWidth = pdfWidth - 2 * margin;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          if (imgHeight < 20) {
            continue;
          }

          if (!isFirstPage) {
            pdf.addPage();
          }
          isFirstPage = false;

          // Render titel native met jsPDF
          let canvasStartY = margin;
          if (titleText) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 102, 204);
            pdf.text(titleText, margin, canvasStartY + 5);
            canvasStartY += 12;
          }

          const currentUsableHeight = pdfHeight - margin - canvasStartY;
          if (imgHeight > currentUsableHeight) {
            const numPages = Math.ceil(imgHeight / currentUsableHeight);

            for (let pageNum = 0; pageNum < numPages; pageNum++) {
              if (pageNum > 0) {
                pdf.addPage();
                canvasStartY = margin;
              }

              const pageUsableHeight = pdfHeight - margin - canvasStartY;
              const sourceY = pageNum === 0 ? 0 : ((currentUsableHeight + (pdfHeight - 2 * margin) * (pageNum - 1)) * canvas.width) / imgWidth;
              const sourceHeight = Math.min(
                (pageUsableHeight * canvas.width) / imgWidth,
                canvas.height - sourceY,
              );

              if (sourceHeight <= 0) break;

              const pageCanvas = document.createElement('canvas');
              const ctx = pageCanvas.getContext('2d');
              pageCanvas.width = canvas.width;
              pageCanvas.height = sourceHeight;

              ctx.drawImage(
                canvas,
                0,
                sourceY,
                canvas.width,
                sourceHeight,
                0,
                0,
                canvas.width,
                sourceHeight,
              );

              const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
              const pageImgHeight = (sourceHeight * imgWidth) / canvas.width;

              pdf.addImage(
                pageImgData,
                'JPEG',
                margin,
                canvasStartY,
                imgWidth,
                pageImgHeight,
              );
            }
          } else {
            pdf.addImage(
              canvas.toDataURL('image/jpeg', 0.95),
              'JPEG',
              margin,
              canvasStartY,
              imgWidth,
              imgHeight,
            );
          }
        }
      }

      reportElement.classList.remove('pdf-rendering');

      // Converteer PDF naar base64 via blob (robuustere methode)
      const pdfBlob = pdf.output('blob');

      // Converteer blob naar base64
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      // Genereer certificaat als optie is aangevinkt
      let certificateBase64 = null;
      if (includeCertificate && template.certificaat) {
        try {
          const certResponse = await fetch(
            `/api/inspections/inspections/${id}/certificate`,
            {
              method: 'GET',
              headers: {
                Authorization: localStorage.getItem('token'),
              },
            },
          );

          if (certResponse.ok) {
            const certBlob = await certResponse.blob();
            certificateBase64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(certBlob);
            });
          }
        } catch (certError) {
          // Certificate generation failed silently
        }
      }

      // Verzend email
      const response = await dispatch(
        sendReportEmail(id, {
          to: emailData.to,
          cc: emailData.cc || undefined,
          subject: emailData.subject,
          body: emailData.body,
          pdfBase64,
          certificateBase64,
          includePublicLink,
        }),
      );

      if (response.success) {
        alert('Rapport succesvol verzonden!');
        setShowEmailModal(false);
        setEmailData({to: '', cc: '', subject: '', body: ''});
        setSelectedTemplate(null);
        setIncludeCertificate(false);
        setIncludePublicLink(false);
      } else {
        alert(response.message || 'Fout bij verzenden');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Fout bij verzenden van email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Load versions list
  const loadVersions = async () => {
    setLoadingVersions(true);
    try {
      const response = await dispatch(getReportVersions(id));
      // Server returns 'results', not 'versions'
      setVersions(response.results || []);
    } catch (error) {
      console.error('Error loading versions:', error);
      alert('Fout bij het laden van versies');
    } finally {
      setLoadingVersions(false);
    }
  };

  // Save current report as new version
  const handleSaveVersion = async () => {
    if (!versionNotes.trim()) {
      alert('Voer een notitie in voor deze versie');
      return;
    }

    setSavingVersion(true);
    try {
      await dispatch(saveReportVersion(id, versionNotes));
      alert('Versie succesvol opgeslagen!');
      setShowSaveModal(false);
      setVersionNotes('');
      loadVersions(); // Refresh versions list
    } catch (error) {
      console.error('Error saving version:', error);
      alert('Fout bij het opslaan van versie');
    } finally {
      setSavingVersion(false);
    }
  };

  // Load a specific version
  const handleLoadVersion = async versionId => {
    try {
      const response = await dispatch(getReportVersion(id, versionId));
      // Server returns 'result', not 'version'
      if (response.result && response.result.report_data) {
        setReportData(response.result.report_data);
        setShowVersionsDropdown(false);
        alert(`Versie ${response.result.version_number} geladen`);
      }
    } catch (error) {
      console.error('Error loading version:', error);
      alert('Fout bij het laden van versie');
    }
  };

  // Load current report (refresh)
  const handleLoadCurrentReport = () => {
    dispatch(generateReport(id)).then(response => {
      if (response && response.result) {
        setReportData(response.result);
        setShowVersionsDropdown(false);
        alert('Huidige rapport geladen');
      }
    });
  };

  if (loading || !reportData) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Laden...</span>
                </div>
                <p className="mt-3">Rapport wordt geladen...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const inspection = reportData.inspection;
  const company = inspection.company_id || {};
  const template = inspection.template_id || {};
  const inspector = inspection.inspector_id || {};

  /**
   * Haal de thumbnail URL op uit een attachment of photo object.
   * Gebruikt medium (720px) als standaard, valt terug op de originele URL.
   */
  const getThumbnailUrl = (attachment, size = 'medium') => {
    if (attachment?.thumbnail) {
      return attachment.thumbnail[size] || attachment.thumbnail.medium || attachment.thumbnail.small || attachment.url || attachment.image;
    }
    return attachment?.url || attachment?.image;
  };

  // Open foto in lightbox (altijd origineel)
  const openLightbox = imageUrl => {
    setLightboxImage(imageUrl);
  };

  // Sluit lightbox
  const closeLightbox = () => {
    setLightboxImage(null);
  };

  return (
    <div className="container-fluid">
      {/* Foto Modal */}
      <Modal
        visible={!!lightboxImage}
        onClose={closeLightbox}
        width="auto"
        height="auto"
        className="photo-modal"
        animation="zoom">
        <div className="photo-modal-content">
          <img src={lightboxImage} alt="Foto" className="photo-modal-image" />
          <div className="photo-modal-actions">
            <a
              href={lightboxImage}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-primary btn-sm">
              <i className="fas fa-external-link-alt mr-2"></i>
              Openen in nieuw tabblad
            </a>
          </div>
        </div>
      </Modal>

      <div className="row">
        <div className="col-12">
          {/* Action buttons - only visible on screen, not in print */}
          <div className="mb-3 no-print d-flex gap-2 align-items-center">
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/zwembadinspecties/inspecties')}>
              <i className="fas fa-arrow-left mr-2"></i>
              Terug naar overzicht
            </button>
            <button
              className="btn btn-primary"
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}>
              {downloadingPDF ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"></span>
                  Downloaden...
                </>
              ) : (
                <>
                  <i className="fas fa-download mr-2"></i>
                  Download als PDF
                </>
              )}
            </button>
            <button className="btn btn-info" onClick={handlePrint}>
              <i className="fas fa-print mr-2"></i>
              Afdrukken
            </button>
            <button className="btn btn-success" onClick={openEmailModal}>
              <i className="fas fa-envelope mr-2"></i>
              Verzend Rapport
            </button>
            <button
              className="btn btn-outline-dark"
              onClick={() => setShowConclusiePanel(!showConclusiePanel)}>
              <i className="fas fa-gavel mr-2"></i>
              Conclusies bewerken
            </button>

            {/* Certificaat button */}
            <button
              className="btn btn-warning"
              onClick={async () => {
                if (template.certificaat) {
                  try {
                    // Download certificaat via API
                    const response = await fetch(
                      `/api/inspections/inspections/${id}/certificate`,
                      {
                        method: 'GET',
                        headers: {
                          Authorization: localStorage.getItem('token'),
                        },
                      },
                    );

                    if (!response.ok) {
                      const errorData = await response.json();
                      alert(
                        errorData.message || 'Fout bij genereren certificaat',
                      );
                      return;
                    }

                    // Download de PDF
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download =
                      response.headers
                        .get('Content-Disposition')
                        ?.split('filename="')[1]
                        ?.replace('"', '') || 'Certificaat.pdf';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } catch (error) {
                    console.error('Error downloading certificate:', error);
                    alert('Fout bij downloaden certificaat');
                  }
                } else {
                  alert(
                    'Er is geen certificaat gekoppeld aan dit sjabloon. Ga naar Sjablonen om een certificaat te koppelen.',
                  );
                }
              }}>
              <i className="fas fa-certificate mr-2"></i>
              Genereer Certificaat
            </button>

            {/* Version management buttons */}
            <div className="ml-auto d-flex gap-2" style={{marginLeft: 'auto'}}>
              <button
                className="btn btn-success"
                onClick={() => setShowSaveModal(true)}>
                <i className="fas fa-save mr-2"></i>
                Opslaan als Versie
              </button>

              <div className="dropdown" style={{position: 'relative'}}>
                <button
                  className="btn btn-warning"
                  onClick={() => {
                    setShowVersionsDropdown(!showVersionsDropdown);
                    if (!showVersionsDropdown) {
                      loadVersions();
                    }
                  }}>
                  <i className="fas fa-history mr-2"></i>
                  Versies Bekijken
                </button>

                {showVersionsDropdown && (
                  <div
                    className="dropdown-menu show"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      maxHeight: '400px',
                      overflowY: 'auto',
                      minWidth: '350px',
                      zIndex: 1000,
                    }}>
                    <div className="dropdown-header d-flex justify-content-between align-items-center">
                      <strong>Rapport Versies</strong>
                      <button
                        type="button"
                        className="close"
                        onClick={() => setShowVersionsDropdown(false)}
                        style={{fontSize: '1.5rem', padding: 0}}>
                        &times;
                      </button>
                    </div>
                    <div className="dropdown-divider"></div>

                    <button
                      className="dropdown-item"
                      onClick={handleLoadCurrentReport}>
                      <i className="fas fa-sync mr-2 text-primary"></i>
                      <strong>Huidige Versie (Actueel)</strong>
                    </button>
                    <div className="dropdown-divider"></div>

                    {loadingVersions ? (
                      <div className="dropdown-item text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status">
                          <span className="sr-only">Laden...</span>
                        </div>
                      </div>
                    ) : versions.length === 0 ? (
                      <div className="dropdown-item text-muted">
                        <i className="fas fa-info-circle mr-2"></i>
                        Geen opgeslagen versies
                      </div>
                    ) : (
                      versions.map(version => (
                        <button
                          key={version._id}
                          className="dropdown-item"
                          onClick={() => handleLoadVersion(version._id)}>
                          <div>
                            <div>
                              <i className="fas fa-file-alt mr-2 text-secondary"></i>
                              <strong>Versie {version.version_number}</strong>
                            </div>
                            <div className="text-muted small">
                              {version.notes}
                            </div>
                            <div className="text-muted small">
                              {new Date(version.created_at).toLocaleDateString(
                                'nl-NL',
                                {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                },
                              )}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Conclusie Selectie Modal */}
          <Modal
            visible={showConclusiePanel}
            onClose={() => setShowConclusiePanel(false)}
            width={700}
          >
            <div className="p-4">
              <h4 className="mb-4">Conclusies Selecteren</h4>

              {conclusieItems.length === 0 ? (
                <div className="text-center py-3">
                  <p className="text-muted">
                    Er zijn nog geen conclusies aangemaakt.
                    <br />
                    <a href="/zwembadinspecties/conclusie-items" target="_blank" rel="noopener noreferrer">
                      Ga naar Conclusies beheer
                    </a>
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-muted mb-3">
                    Selecteer de conclusies die van toepassing zijn op dit rapport:
                  </p>

                  {/* Tag filter */}
                  {(() => {
                    const tags = [...new Set(conclusieItems.map(item => item.tag).filter(Boolean))];
                    if (tags.length === 0) return null;
                    return (
                      <div className="mb-3">
                        <button
                          className={`btn btn-sm mr-2 mb-1 ${!conclusieTagFilter ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setConclusieTagFilter('')}>
                          Alle
                        </button>
                        {tags.map(tag => (
                          <button
                            key={tag}
                            className={`btn btn-sm mr-2 mb-1 ${conclusieTagFilter === tag ? 'btn-info' : 'btn-outline-info'}`}
                            onClick={() => setConclusieTagFilter(conclusieTagFilter === tag ? '' : tag)}>
                            {tag}
                          </button>
                        ))}
                      </div>
                    );
                  })()}

                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th style={{width: '40px'}}></th>
                          <th>Conclusie</th>
                          <th style={{width: '100px'}}>Tag</th>
                          <th style={{width: '130px'}}>Certificaat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conclusieItems
                          .filter(item => !conclusieTagFilter || item.tag === conclusieTagFilter)
                          .map(item => (
                          <tr
                            key={item._id}
                            style={{cursor: 'pointer'}}
                            className={selectedConclusies.includes(item._id) ? 'table-primary' : ''}
                            onClick={() => handleToggleConclusie(item._id)}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedConclusies.includes(item._id)}
                                onChange={() => handleToggleConclusie(item._id)}
                              />
                            </td>
                            <td>
                              {item.beschrijving && (
                                <div className="mb-1"><strong>{item.beschrijving}</strong></div>
                              )}
                              <span style={{whiteSpace: 'pre-wrap'}}>{item.text}</span>
                            </td>
                            <td>
                              {item.tag ? (
                                <span className="badge badge-info">{item.tag}</span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {item.certificaat_uitgeven ? (
                                <span className="badge badge-success">
                                  <i className="fas fa-check mr-1"></i>Ja
                                </span>
                              ) : (
                                <span className="badge badge-secondary">Nee</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="form-group">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="certificaatUitgevenOverride"
                        checked={certificaatUitgeven || false}
                        onChange={(e) => setCertificaatUitgeven(e.target.checked)}
                      />
                      <label className="custom-control-label" htmlFor="certificaatUitgevenOverride">
                        <strong>Certificaat uitgeven</strong>
                        <br />
                        <small className="text-muted">Wordt automatisch bepaald op basis van geselecteerde conclusies</small>
                      </label>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button
                      className="btn btn-secondary mr-2"
                      onClick={() => setShowConclusiePanel(false)}>
                      Annuleren
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveConclusies}
                      disabled={savingConclusies}>
                      {savingConclusies ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2"></span>
                          Opslaan...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save mr-2"></i>
                          Opslaan
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </Modal>

          {/* Save Version Modal */}
          {showSaveModal && (
            <div
              className="modal show d-block"
              style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
              onClick={() => setShowSaveModal(false)}>
              <div
                className="modal-dialog modal-dialog-centered"
                onClick={e => e.stopPropagation()}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Rapport Versie Opslaan</h5>
                    <button
                      type="button"
                      className="close"
                      onClick={() => setShowSaveModal(false)}>
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <p>
                      Sla de huidige staat van het rapport op als een nieuwe
                      versie.
                    </p>
                    <div className="form-group">
                      <label htmlFor="versionNotes">
                        <strong>Notitie voor deze versie:</strong>
                      </label>
                      <input
                        type="text"
                        id="versionNotes"
                        className="form-control"
                        placeholder="Bijv. 'Na eerste feedback' of 'Definitieve versie'"
                        value={versionNotes}
                        onChange={e => setVersionNotes(e.target.value)}
                        onKeyPress={e => {
                          if (e.key === 'Enter') {
                            handleSaveVersion();
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowSaveModal(false)}>
                      Annuleren
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleSaveVersion}
                      disabled={savingVersion || !versionNotes.trim()}>
                      {savingVersion ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"></span>
                          Opslaan...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save mr-2"></i>
                          Versie Opslaan
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Send Email Modal */}
          <Modal
            visible={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            width={700}
            className="email-send-modal">
            <div className="p-4">
              <h4 className="mb-4">
                <i className="fas fa-envelope mr-2"></i>
                Rapport Verzenden
              </h4>

              {loadingTemplates ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Laden...</span>
                  </div>
                  <p className="mt-2">Sjablonen laden...</p>
                </div>
              ) : (
                <>
                  {/* Template selectie */}
                  {emailTemplates.length > 0 && (
                    <div className="form-group">
                      <label>
                        <strong>E-mail Sjabloon</strong>
                      </label>
                      <select
                        className="form-control"
                        value={selectedTemplate?._id || ''}
                        onChange={e => {
                          const template = emailTemplates.find(
                            t => t._id === e.target.value,
                          );
                          if (template) selectEmailTemplate(template);
                        }}>
                        {emailTemplates.map(template => (
                          <option key={template._id} value={template._id}>
                            {template.name}{' '}
                            {template.is_default ? '(Standaard)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <hr />

                  {/* Ontvanger */}
                  <div className="form-group">
                    <label>
                      <strong>Aan *</strong>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="email@voorbeeld.nl"
                      value={emailData.to}
                      onChange={e =>
                        setEmailData({...emailData, to: e.target.value})
                      }
                    />
                  </div>

                  {/* CC */}
                  <div className="form-group">
                    <label>
                      <strong>CC</strong>{' '}
                      <span className="text-muted">(optioneel)</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="cc@voorbeeld.nl"
                      value={emailData.cc}
                      onChange={e =>
                        setEmailData({...emailData, cc: e.target.value})
                      }
                    />
                  </div>

                  {/* Onderwerp */}
                  <div className="form-group">
                    <label>
                      <strong>Onderwerp *</strong>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Onderwerp van de email"
                      value={emailData.subject}
                      onChange={e =>
                        setEmailData({...emailData, subject: e.target.value})
                      }
                    />
                  </div>

                  {/* Bericht */}
                  <div className="form-group">
                    <label>
                      <strong>Bericht *</strong>
                    </label>
                    <textarea
                      className="form-control"
                      rows={10}
                      placeholder="Schrijf hier je bericht..."
                      value={emailData.body}
                      onChange={e =>
                        setEmailData({...emailData, body: e.target.value})
                      }
                    />
                  </div>

                  {/* Bijlage info */}
                  <div className="alert alert-info">
                    <i className="fas fa-paperclip mr-2"></i>
                    <strong>Bijlage:</strong> Het PDF-rapport wordt automatisch
                    als bijlage toegevoegd.
                  </div>

                  {/* Online rapport link optie */}
                  <div className="form-group">
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="includePublicLink"
                        checked={includePublicLink}
                        onChange={e =>
                          setIncludePublicLink(e.target.checked)
                        }
                      />
                      <label
                        className="custom-control-label"
                        htmlFor="includePublicLink">
                        <i className="fas fa-link mr-2 text-success"></i>
                        <strong>Online rapport link toevoegen</strong>
                        <br />
                        <small className="text-muted">
                          Een link wordt aan de e-mail toegevoegd zodat de ontvanger het rapport online kan bekijken
                        </small>
                      </label>
                    </div>
                    {includePublicLink && publicReportUrl && (
                      <div className="mt-2 ml-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={publicReportUrl}
                            readOnly
                            style={{fontSize: '0.85rem'}}
                          />
                          <div className="input-group-append">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(publicReportUrl);
                                alert('Link gekopieerd naar klembord!');
                              }}>
                              <i className="fas fa-copy"></i>
                            </button>
                            <a
                              href={publicReportUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline-primary btn-sm">
                              <i className="fas fa-external-link-alt"></i>
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Certificaat optie - alleen tonen als er een certificaat gekoppeld is */}
                  {template.certificaat && (
                    <div className="form-group">
                      <div className="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="includeCertificate"
                          checked={includeCertificate}
                          onChange={e =>
                            setIncludeCertificate(e.target.checked)
                          }
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="includeCertificate">
                          <i className="fas fa-certificate mr-2 text-warning"></i>
                          <strong>Certificaat als bijlage toevoegen</strong>
                          <br />
                          <small className="text-muted">
                            Het certificaat "{template.certificaat.name}" wordt
                            als extra PDF bijlage meegestuurd
                          </small>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button
                      className="btn btn-secondary mr-2"
                      onClick={() => setShowEmailModal(false)}
                      disabled={sendingEmail}>
                      Annuleren
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={handleSendEmail}
                      disabled={
                        sendingEmail ||
                        !emailData.to ||
                        !emailData.subject ||
                        !emailData.body
                      }>
                      {sendingEmail ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2"></span>
                          Verzenden...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane mr-2"></i>
                          Verzenden
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </Modal>

          {/* Report content */}
          <div className="report-container" ref={reportRef}>
            {/* Cover page */}
            <div className="report-page report-cover">
              <div className="report-header">
                <div className="report-year">{inspection.inspection_date ? new Date(inspection.inspection_date).getFullYear() : new Date().getFullYear()}</div>
                <div className="report-brand">
                  <div className="brand-logo">
                    <img
                      src="/brand/logo_small.png"
                      alt="Zwembadinspecties.nl"
                      className="report-logo"
                    />
                  </div>
                  <div className="brand-info">
                    <p>Zwembadinspecties BV</p>
                    <p>(0570) 76 33 22</p>
                    <p>info@zwembadinspecties.nl</p>
                    <p>K.v.K. 96.80.21.89</p>
                  </div>
                </div>
              </div>

              <div className="report-title-section">
                <h1 className="report-main-title">
                  {inspection.title ||
                    `${template.category?.toUpperCase()} - ${company.name?.toUpperCase()}`}
                </h1>
                <p className="report-subtitle">
                  {company.name} - {formatDate(inspection.inspection_date)}
                </p>
                <p className="report-subtitle">
                  Dit inspectierapport omvat de constateringen die zijn gedaan
                  tijdens de inspectie op d.d.{' '}
                  {formatDate(inspection.inspection_date)}
                </p>

                {/* Main photo - prioritize report photo, fallback to first result with attachments */}
                {(() => {
                  // First check if there's a report photo
                  if (
                    inspection.report_photo &&
                    inspection.report_photo.image
                  ) {
                    return (
                      <div className="report-main-photo">
                        <img
                          src={getThumbnailUrl(inspection.report_photo, 'large')}
                          alt="Rapport foto"
                        />
                      </div>
                    );
                  }

                  // Fallback to first photo from results
                  const firstPhotoResult = reportData.results?.find(
                    r => r.attachments && r.attachments.length > 0,
                  );
                  if (firstPhotoResult && firstPhotoResult.attachments[0]) {
                    return (
                      <div className="report-main-photo">
                        <img
                          src={getThumbnailUrl(firstPhotoResult.attachments[0], 'large')}
                          alt="Inspectie foto"
                        />
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            {/* Info page */}
            <div className="report-page">
              {/* Information grid */}
              <div className="report-info-grid">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Object</span>
                        <span className="info-value">
                          {template.name || '-'}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Type zwembad</span>
                        <span className="info-value">
                          {company.pool_type || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Inspectietype</span>
                        <span className="info-value">
                          {template.category || '-'}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Rapportnummer</span>
                        <span className="info-value">
                          ZB{new Date().getFullYear()} -{' '}
                          {inspection._id?.slice(-8)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Eigenaar</span>
                        <span className="info-value">
                          {company.name || '-'}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Adres</span>
                        <span className="info-value">
                          {company.street && company.house_number
                            ? `${company.street} ${company.house_number}`
                            : '-'}
                          {company.postal_code && company.city && (
                            <>
                              , {company.postal_code} {company.city}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Contactpersoon</span>
                        <span className="info-value">
                          {company.contact_name || '-'}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Contactgegevens</span>
                        <span className="info-value">
                          {company.contact_phone &&
                            `Tel. ${company.contact_phone}`}
                          {company.contact_email && (
                            <>
                              {company.contact_phone && <br />}
                              {company.contact_email}
                            </>
                          )}
                          {!company.contact_phone &&
                            !company.contact_email &&
                            '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Inspecteur</span>
                        <span className="info-value">
                          {inspector.voornaam} {inspector.achternaam}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Inspectiedatum</span>
                        <span className="info-value">
                          {formatDate(inspection.inspection_date)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Status</span>
                        <span className="info-value">
                          {inspection.status === 'completed' && 'Voltooid'}
                          {inspection.status === 'approved' && 'Goedgekeurd'}
                          {inspection.status === 'in_progress' &&
                            'In uitvoering'}
                          {inspection.status === 'draft' && 'Concept'}
                          {inspection.status === 'rejected' && 'Afgekeurd'}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="info-item">
                        <span className="info-label">Conclusie</span>
                        <span className="info-value">
                          {(() => {
                            const selectedConclusieObjects = getSelectedConclusieObjects();
                            if (selectedConclusieObjects.length > 0) {
                              const summary = selectedConclusieObjects.map(c => c.beschrijving || c.text).join(', ');
                              return summary.length > 120 ? summary.substring(0, 120) + '...' : summary;
                            }
                            return 'Nog geen conclusies geselecteerd.';
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
            </div>

            {/* Table of contents */}
            <div className="report-page">
              <h2 className="section-title">Inhoudsopgave</h2>
              <div className="toc">
                {/* Introductie - alleen tonen als er een introduction is */}
                {template &&
                  template.introduction &&
                  template.introduction.trim() && (
                    <div className="toc-item">
                      <span>1.</span>
                      <span>Introductie</span>
                      <span className="toc-dots"></span>
                      <span>Blz. 2</span>
                    </div>
                  )}

                {/* Bevindingen per hoofdstuk */}
                {template &&
                template.chapters &&
                template.chapters.length > 0 ? (
                  template.chapters
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((chapter, index) => (
                      <div className="toc-item" key={index}>
                        <span>
                          {template.introduction && template.introduction.trim()
                            ? index + 2
                            : index + 1}
                          .
                        </span>
                        <span>{chapter.title}</span>
                        <span className="toc-dots"></span>
                        <span>
                          Blz.{' '}
                          {template.introduction && template.introduction.trim()
                            ? index + 3
                            : index + 2}
                        </span>
                      </div>
                    ))
                ) : (
                  /* Fallback: toon bevindingen zonder hoofdstukken */
                  <div className="toc-item">
                    <span>
                      {template.introduction && template.introduction.trim()
                        ? 2
                        : 1}
                      .
                    </span>
                    <span>Bevindingen</span>
                    <span className="toc-dots"></span>
                    <span>
                      Blz.{' '}
                      {template.introduction && template.introduction.trim()
                        ? 3
                        : 2}
                    </span>
                  </div>
                )}

                {/* Conclusie */}
                <div className="toc-item">
                  <span>
                    {(template?.introduction && template.introduction.trim()
                      ? 1
                      : 0) +
                      (template?.chapters?.length || 1) +
                      1}
                    .
                  </span>
                  <span>Conclusie</span>
                  <span className="toc-dots"></span>
                  <span>
                    Blz.{' '}
                    {(template?.introduction && template.introduction.trim()
                      ? 1
                      : 0) +
                      (template?.chapters?.length || 1) +
                      2}
                  </span>
                </div>
              </div>
            </div>

            {/* Introductie - alleen tonen als template een introduction heeft */}
            {template &&
              template.introduction &&
              template.introduction.trim() && (
                <div className="report-page">
                  <h2 className="section-title">1. Introductie</h2>
                  <div
                    dangerouslySetInnerHTML={{__html: template.introduction}}
                  />
                </div>
              )}

            {/* Bevindingen per hoofdstuk */}
            {template && template.chapters && template.chapters.length > 0 ? (
              template.chapters
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((chapter, chapterIndex) => {
                  const sectionNumber =
                    (template.introduction && template.introduction.trim()
                      ? 1
                      : 0) +
                    chapterIndex +
                    1;

                  // Filter bevindingen voor dit hoofdstuk met verbeterde matching
                  const chapterFindings =
                    reportData.results?.filter(r => {
                      if (!r.template_item_id) return false;
                      const itemChapterId = r.template_item_id.chapter_id;
                      if (!itemChapterId) return false;

                      // Probeer verschillende matching strategieën
                      const chapterId = chapter._id;

                      // Direct match
                      if (itemChapterId === chapterId) return true;

                      // String comparison
                      if (String(itemChapterId) === String(chapterId))
                        return true;

                      // Index-based match (fallback voor oude data)
                      if (
                        itemChapterId === chapterIndex.toString() ||
                        itemChapterId === String(chapterIndex)
                      )
                        return true;

                      return false;
                    }) || [];

                  return (
                    <div key={chapterIndex} className="report-page">
                      <h2 className="section-title">
                        {sectionNumber}. {chapter.title}
                      </h2>

                      {/* Chapter description */}
                      {chapter.description && chapter.description.trim() && (
                        <div
                          className="chapter-description"
                          dangerouslySetInnerHTML={{
                            __html: chapter.description,
                          }}
                        />
                      )}

                      {/* Bevindingen tabel voor dit hoofdstuk */}
                      {(() => {
                        // Check of er items zijn met foto's/attachments
                        const hasAnyPhotos = chapterFindings.some(
                          f => f.attachments && f.attachments.length > 0,
                        );
                        const showPhotoColumn = hasAnyPhotos;

                        return (
                          <table className="findings-table">
                            <thead>
                              <tr>
                                <th
                                  style={{
                                    width: showPhotoColumn ? '30%' : '40%',
                                  }}>
                                  Item
                                </th>
                                <th
                                  style={{
                                    width: showPhotoColumn ? '25%' : '60%',
                                  }}>
                                  Antwoord
                                </th>
                                {showPhotoColumn && (
                                  <th style={{width: '35%'}}>Foto</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {chapterFindings.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={showPhotoColumn ? 3 : 2}
                                    className="text-center text-muted">
                                    Geen items gevonden voor dit hoofdstuk
                                  </td>
                                </tr>
                              ) : (
                                chapterFindings.map((finding, findingIndex) => {
                                  const item = finding.template_item_id;

                                  // Special rendering for inspectieonderdelen
                                  if (
                                    finding.is_inspectieonderdelen &&
                                    Array.isArray(finding.value)
                                  ) {
                                    return (
                                      <tr key={findingIndex}>
                                        <td
                                          colSpan={showPhotoColumn ? 3 : 2}
                                          style={{padding: 0}}>
                                          <div style={{padding: '12px'}}>
                                            <strong
                                              style={{
                                                display: 'block',
                                                marginBottom: '12px',
                                              }}>
                                              {item?.question}
                                            </strong>
                                            <table
                                              className="table table-sm table-bordered"
                                              style={{marginBottom: 0}}>
                                              <thead
                                                style={{
                                                  backgroundColor: '#f8f9fa',
                                                }}>
                                                <tr>
                                                  <th style={{width: '20%'}}>
                                                    Inspectieonderdeel
                                                  </th>
                                                  <th style={{width: '25%'}}>
                                                    Eis/Toelichting
                                                  </th>
                                                  <th style={{width: '15%'}}>
                                                    Score
                                                  </th>
                                                  <th style={{width: '25%'}}>
                                                    Test
                                                  </th>
                                                  <th style={{width: '15%'}}>
                                                    Foto
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {finding.value.map(
                                                  (row, rowIndex) => (
                                                    <tr
                                                      key={rowIndex}
                                                      className={
                                                        row.has_deviation
                                                          ? 'row-with-deviation'
                                                          : ''
                                                      }>
                                                      <td>
                                                        {row.inspectieonderdeel}
                                                      </td>
                                                      <td>
                                                        {row.eis_toelichting}
                                                      </td>
                                                      <td>
                                                        {row.score !== '-' ? (
                                                          <div>
                                                            <span
                                                              className={`badge ${
                                                                row.score ===
                                                                '3'
                                                                  ? 'badge-secondary'
                                                                  : row.score ===
                                                                    '2'
                                                                  ? 'badge-danger'
                                                                  : 'badge-success'
                                                              }`}>
                                                              {row.score === '1'
                                                                ? '1. Voldoende'
                                                                : row.score ===
                                                                  '2'
                                                                ? '2. Onvoldoende'
                                                                : '3. N.v.t.'}
                                                            </span>
                                                            {row.has_deviation && (
                                                              <div className="mt-1">
                                                                <span
                                                                  className={`severity-badge severity-${row.deviation_severity}`}>
                                                                  ⚠{' '}
                                                                  {row.deviation_severity ===
                                                                  'high'
                                                                    ? 'Hoog'
                                                                    : row.deviation_severity ===
                                                                      'medium'
                                                                    ? 'Gemiddeld'
                                                                    : 'Laag'}
                                                                </span>
                                                              </div>
                                                            )}
                                                          </div>
                                                        ) : (
                                                          <span className="text-muted">
                                                            -
                                                          </span>
                                                        )}
                                                      </td>
                                                      <td>
                                                        {row.score === '2' &&
                                                        row.test ? (
                                                          <div
                                                            style={{
                                                              fontSize: '12px',
                                                              whiteSpace:
                                                                'pre-wrap',
                                                            }}>
                                                            {row.test}
                                                          </div>
                                                        ) : (
                                                          <span className="text-muted">
                                                            -
                                                          </span>
                                                        )}
                                                      </td>
                                                      <td className="photo-cell">
                                                        {row.attachments &&
                                                        row.attachments.length >
                                                          0 ? (
                                                          <div className="finding-photos">
                                                            {row.attachments.map(
                                                              (
                                                                attachment,
                                                                photoIndex,
                                                              ) => (
                                                                <div
                                                                  key={
                                                                    photoIndex
                                                                  }
                                                                  style={{
                                                                    marginBottom:
                                                                      '8px',
                                                                    textAlign:
                                                                      'center',
                                                                  }}>
                                                                  <img
                                                                    src={
                                                                      getThumbnailUrl(attachment)
                                                                    }
                                                                    alt={`Foto ${
                                                                      rowIndex +
                                                                      1
                                                                    }`}
                                                                    className="finding-photo"
                                                                    style={{
                                                                      maxWidth:
                                                                        '180px',
                                                                      maxHeight:
                                                                        '180px',
                                                                      cursor:
                                                                        'pointer',
                                                                      display:
                                                                        'block',
                                                                      margin:
                                                                        '0 auto',
                                                                    }}
                                                                    onClick={() =>
                                                                      openLightbox(
                                                                        attachment.url,
                                                                      )
                                                                    }
                                                                  />
                                                                  {attachment.type && (
                                                                    <div
                                                                      style={{
                                                                        fontSize:
                                                                          '9px',
                                                                        color:
                                                                          '#666',
                                                                        marginTop:
                                                                          '2px',
                                                                        fontWeight:
                                                                          '600',
                                                                      }}>
                                                                      {
                                                                        attachment.type
                                                                      }
                                                                    </div>
                                                                  )}
                                                                </div>
                                                              ),
                                                            )}
                                                          </div>
                                                        ) : (
                                                          <span className="text-muted">
                                                            -
                                                          </span>
                                                        )}
                                                      </td>
                                                    </tr>
                                                  ),
                                                )}
                                              </tbody>
                                            </table>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  }

                                  // Special rendering for schaaldelen
                                  if (
                                    finding.is_schaaldelen &&
                                    Array.isArray(finding.value)
                                  ) {
                                    return (
                                      <tr key={findingIndex}>
                                        <td
                                          colSpan={showPhotoColumn ? 3 : 2}
                                          style={{padding: 0}}>
                                          <div className="schaaldelen-overview">
                                            <strong
                                              style={{
                                                display: 'block',
                                                marginBottom: '12px',
                                                padding: '12px 12px 0 12px',
                                              }}>
                                              {item?.question}
                                            </strong>
                                            <table>
                                              <thead>
                                                <tr>
                                                  <th>Nr.</th>
                                                  <th>Naad</th>
                                                  <th>Deel</th>
                                                  <th>Foto's</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {finding.value.map(
                                                  (row, rowIndex) => (
                                                    <tr
                                                      key={rowIndex}
                                                      style={
                                                        row.has_deviation
                                                          ? {
                                                              backgroundColor:
                                                                'rgb(255, 243, 205)',
                                                            }
                                                          : {}
                                                      }>
                                                      <td>
                                                        <strong>
                                                          {row.item_number}
                                                        </strong>
                                                      </td>
                                                      <td>
                                                        <span
                                                          className={`badge ${
                                                            row.status_naad ===
                                                            'OK'
                                                              ? 'badge-success'
                                                              : row.status_naad ===
                                                                'NIET OK'
                                                              ? 'badge-danger'
                                                              : 'badge-secondary'
                                                          }`}>
                                                          {row.status_naad}
                                                        </span>
                                                      </td>
                                                      <td>
                                                        <span
                                                          className={`badge ${
                                                            row.status_deel ===
                                                            'OK'
                                                              ? 'badge-success'
                                                              : row.status_deel ===
                                                                'NIET OK'
                                                              ? 'badge-danger'
                                                              : 'badge-secondary'
                                                          }`}>
                                                          {row.status_deel}
                                                        </span>
                                                      </td>
                                                      <td className="photo-cell">
                                                        {row.attachments &&
                                                        row.attachments.length >
                                                          0 ? (
                                                          <div className="finding-photos">
                                                            {row.attachments.map(
                                                              (
                                                                attachment,
                                                                photoIndex,
                                                              ) => (
                                                                <div
                                                                  key={
                                                                    photoIndex
                                                                  }
                                                                  style={{
                                                                    display:
                                                                      'inline-block',
                                                                    marginRight:
                                                                      '8px',
                                                                    marginBottom:
                                                                      '8px',
                                                                    textAlign:
                                                                      'center',
                                                                  }}>
                                                                  <img
                                                                    src={
                                                                      getThumbnailUrl(attachment)
                                                                    }
                                                                    alt={`${attachment.type} ${row.item_number}`}
                                                                    className="finding-photo"
                                                                    style={{
                                                                      maxWidth:
                                                                        '120px',
                                                                      maxHeight:
                                                                        '120px',
                                                                      cursor:
                                                                        'pointer',
                                                                      display:
                                                                        'block',
                                                                    }}
                                                                    onClick={() =>
                                                                      openLightbox(
                                                                        attachment.url,
                                                                      )
                                                                    }
                                                                  />
                                                                  {attachment.type && (
                                                                    <div
                                                                      style={{
                                                                        fontSize:
                                                                          '9px',
                                                                        color:
                                                                          '#666',
                                                                        marginTop:
                                                                          '2px',
                                                                        fontWeight:
                                                                          '600',
                                                                      }}>
                                                                      {
                                                                        attachment.type
                                                                      }
                                                                    </div>
                                                                  )}
                                                                </div>
                                                              ),
                                                            )}
                                                          </div>
                                                        ) : (
                                                          <span className="text-muted">
                                                            -
                                                          </span>
                                                        )}
                                                      </td>
                                                    </tr>
                                                  ),
                                                )}
                                              </tbody>
                                            </table>
                                            {finding.has_deviation && (
                                              <div
                                                style={{
                                                  marginTop: '8px',
                                                  padding: '8px 12px 12px 12px',
                                                  fontSize: '12px',
                                                  color: '#856404',
                                                }}>
                                                <i className="fas fa-exclamation-triangle mr-1"></i>
                                                Er zijn afwijkingen
                                                geconstateerd
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  }

                                  return (
                                    <tr
                                      key={findingIndex}
                                      className={
                                        finding.has_deviation
                                          ? 'row-with-deviation'
                                          : ''
                                      }>
                                      {/* Item/Vraag */}
                                      <td>
                                        {item && item.question ? (
                                          <div>
                                            <strong>{item.question}</strong>
                                            {item.help_text &&
                                              item.input_type !==
                                                'checkbox' && (
                                                <div className="text-muted small mt-1">
                                                  {item.help_text}
                                                </div>
                                              )}
                                          </div>
                                        ) : (
                                          '-'
                                        )}
                                      </td>

                                      {/* Antwoord */}
                                      <td>
                                        {((finding.value != null && finding.value !== '') || item?.input_type === 'checkbox') &&
                                        item?.input_type !== 'file' ? (
                                          <div>
                                            {item?.input_type === 'checkbox' ? (
                                              <span
                                                className={`badge ${
                                                  finding.value === 'true' ||
                                                  finding.value === true
                                                    ? 'badge-success'
                                                    : 'badge-secondary'
                                                }`}>
                                                {finding.value === 'true' ||
                                                finding.value === true
                                                  ? 'Ja'
                                                  : 'Nee'}
                                              </span>
                                            ) : item?.input_type ===
                                              'bevindingen' ? (
                                              // Voor bevindingen velden tonen we het risico niveau en de bevindingen tekst
                                              (() => {
                                                // OK - Geen bevindingen
                                                if (finding.value === 'OK - Geen bevindingen') {
                                                  return (
                                                    <span className="badge badge-success">
                                                      <i className="fas fa-check mr-1"></i>
                                                      OK
                                                    </span>
                                                  );
                                                }

                                                // OK na bevindingen
                                                if (finding.value === 'OK na bevindingen') {
                                                  return (
                                                    <div>
                                                      <span className="badge badge-warning">
                                                        <i className="fas fa-check-double mr-1"></i>
                                                        OK na bevindingen
                                                      </span>
                                                      {finding.remarks && (
                                                        <div
                                                          style={{
                                                            fontSize: '12px',
                                                            fontStyle: 'italic',
                                                            whiteSpace: 'pre-wrap',
                                                            color: '#333',
                                                            marginTop: '8px',
                                                          }}>
                                                          {finding.remarks}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                }

                                                if (
                                                  finding.value &&
                                                  finding.value.includes(
                                                    'Risico:',
                                                  )
                                                ) {
                                                  // Bepaal badge kleur op basis van risico
                                                  let badgeClass =
                                                    'badge-secondary';
                                                  let icon = '⚠';

                                                  if (
                                                    finding.value.includes(
                                                      'Zeer hoog',
                                                    ) ||
                                                    finding.value.includes(
                                                      'Risico: 1',
                                                    )
                                                  ) {
                                                    badgeClass = 'badge-danger';
                                                    icon = '🚨';
                                                  } else if (
                                                    finding.value.includes(
                                                      'Hoog',
                                                    ) ||
                                                    finding.value.includes(
                                                      'Risico: 2',
                                                    )
                                                  ) {
                                                    badgeClass = 'badge-danger';
                                                    icon = '⚠';
                                                  } else if (
                                                    finding.value.includes(
                                                      'Gemiddeld',
                                                    ) ||
                                                    finding.value.includes(
                                                      'Risico: 3',
                                                    )
                                                  ) {
                                                    badgeClass =
                                                      'badge-warning';
                                                    icon = '⚠';
                                                  } else if (
                                                    finding.value.includes(
                                                      'Laag',
                                                    )
                                                  ) {
                                                    badgeClass = 'badge-info';
                                                    icon = 'ℹ';
                                                  }

                                                  // Parse remarks to separate bevindingen text and advies
                                                  const parseRemarks = (remarks) => {
                                                    if (!remarks) return { bevindingen: '', advies: '' };

                                                    // Check if remarks contains "Advies:" to split
                                                    const adviesIndex = remarks.indexOf('Advies:');
                                                    if (adviesIndex !== -1) {
                                                      return {
                                                        bevindingen: remarks.substring(0, adviesIndex).trim(),
                                                        advies: remarks.substring(adviesIndex).trim()
                                                      };
                                                    }
                                                    return { bevindingen: remarks, advies: '' };
                                                  };

                                                  const { bevindingen: bevindingenText, advies } = parseRemarks(finding.remarks);

                                                  return (
                                                    <div>
                                                      {/* Risico als platte tekst */}
                                                      <div
                                                        style={{
                                                          fontSize: '12px',
                                                          marginBottom: '12px',
                                                        }}>
                                                        {finding.value}
                                                      </div>

                                                      {/* Bevindingen tekst in cursief */}
                                                      {bevindingenText && (
                                                        <div
                                                          style={{
                                                            fontSize: '12px',
                                                            fontStyle: 'italic',
                                                            whiteSpace: 'pre-wrap',
                                                            color: '#333',
                                                            marginBottom: advies ? '12px' : '0',
                                                          }}>
                                                          {bevindingenText}
                                                        </div>
                                                      )}

                                                      {/* Advies sectie in cursief */}
                                                      {advies && (
                                                        <div
                                                          style={{
                                                            fontSize: '12px',
                                                            fontStyle: 'italic',
                                                            whiteSpace: 'pre-wrap',
                                                            color: '#333',
                                                          }}>
                                                          {advies}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                }
                                                return (
                                                  <span className="text-muted">
                                                    {finding.value ||
                                                      'Bevinding'}
                                                  </span>
                                                );
                                              })()
                                            ) : typeof finding.value ===
                                              'object' ? (
                                              // Voor andere objecten tonen we JSON of niets
                                              <span className="text-muted">
                                                -
                                              </span>
                                            ) : (
                                              // Voor gewone string/number waarden
                                              <span>{finding.value}</span>
                                            )}
                                            {/* Toon remarks voor alle item types (behalve bevindingen, die worden al hierboven getoond) */}
                                            {item?.input_type !==
                                              'bevindingen' &&
                                              finding.remarks && (
                                                <div
                                                  style={{
                                                    marginTop: '8px',
                                                    fontSize: '12px',
                                                    whiteSpace: 'pre-wrap',
                                                    color: '#333',
                                                    fontStyle: 'italic',
                                                  }}>
                                                  {finding.remarks}
                                                </div>
                                              )}
                                          </div>
                                        ) : (
                                          <>
                                            <span className="text-muted">
                                              -
                                            </span>
                                            {finding.remarks && (
                                              <div
                                                style={{
                                                  marginTop: '8px',
                                                  fontSize: '12px',
                                                  whiteSpace: 'pre-wrap',
                                                  color: '#333',
                                                  fontStyle: 'italic',
                                                }}>
                                                {finding.remarks}
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </td>

                                      {/* Foto's - alleen tonen als showPhotoColumn true is */}
                                      {showPhotoColumn && (
                                        <td className="photo-cell">
                                          {finding.attachments &&
                                          finding.attachments.length > 0 ? (
                                            <div className="finding-photos">
                                              {finding.attachments.map(
                                                (attachment, photoIndex) => (
                                                  <div
                                                    key={photoIndex}
                                                    style={{
                                                      marginBottom: '8px',
                                                      textAlign: 'center',
                                                    }}>
                                                    <img
                                                      src={getThumbnailUrl(attachment)}
                                                      alt={`Foto ${
                                                        findingIndex + 1
                                                      }`}
                                                      className="finding-photo"
                                                      style={{
                                                        maxWidth: '180px',
                                                        maxHeight: '180px',
                                                        cursor: 'pointer',
                                                        display: 'block',
                                                        margin: '0 auto',
                                                      }}
                                                      onClick={() =>
                                                        openLightbox(
                                                          attachment.url,
                                                        )
                                                      }
                                                    />
                                                    {attachment.type && (
                                                      <div
                                                        style={{
                                                          fontSize: '10px',
                                                          color: '#666',
                                                          marginTop: '4px',
                                                          fontWeight: '600',
                                                        }}>
                                                        {attachment.type}
                                                      </div>
                                                    )}
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          ) : (
                                            <span className="text-muted">
                                              -
                                            </span>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        );
                      })()}
                    </div>
                  );
                })
            ) : (
              /* Fallback: als er geen chapters zijn, toon alle bevindingen in één lijst */
              <div className="report-page">
                <h2 className="section-title">
                  {template?.introduction && template.introduction.trim()
                    ? 2
                    : 1}
                  . Bevindingen
                </h2>
                <p className="text-muted">
                  Geen hoofdstukken gedefinieerd in de template.
                </p>
              </div>
            )}

            {/* Conclusie */}
            <div className="report-page">
              <h2 className="section-title">
                {(template?.introduction && template.introduction.trim()
                  ? 1
                  : 0) +
                  (template?.chapters?.length || 1) +
                  1}
                . Conclusie
              </h2>

              {(() => {
                const selectedConclusieObjects = getSelectedConclusieObjects();
                const inspectionDateStr = formatDate(inspection?.inspection_date);

                if (selectedConclusieObjects.length > 0) {
                  // Toon geselecteerde conclusies
                  return (
                    <div>
                      {selectedConclusieObjects.map((conclusie, idx) => (
                        <p key={conclusie._id || idx} style={{marginBottom: '10px', whiteSpace: 'pre-wrap'}}>
                          {(conclusie.text || '').replace(/\{\{inspectiedatum\}\}/g, inspectionDateStr)}
                        </p>
                      ))}
                    </div>
                  );
                } else {
                  // Geen conclusies geselecteerd - toon melding
                  return (
                    <p className="text-muted">
                      <em>Er zijn nog geen conclusies geselecteerd voor dit rapport. Gebruik de knop "Conclusies bewerken" om conclusies toe te voegen.</em>
                    </p>
                  );
                }
              })()}

              {inspection.general_remarks && (
                <div className="general-remarks">
                  <h3>Algemene Opmerkingen</h3>
                  <p style={{whiteSpace: 'pre-wrap'}}>
                    {inspection.general_remarks}
                  </p>
                </div>
              )}

              {/* Toon datum van afronding */}
              {inspection.end_time && (
                <div
                  className="completion-info"
                  style={{marginTop: '30px', fontSize: '13px', color: '#666'}}>
                  <p>
                    Inspectie afgerond op: {formatDate(inspection.end_time)}
                  </p>
                  {inspection.signature_url && (
                    <p>Inspectie is digitaal ondertekend.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionReportPage;
